"use client";

import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import Image from "next/image";
import { track } from "@vercel/analytics";
import { Magnetic } from "./Magnetic";
import { Section } from "./Section";
import { LockIcon, ArrowRightIcon, BackIcon } from "./Button";
import { useMotionMode } from "@/context/MotionContext";
import type { Project } from "@/data/projects";
import type { ThemeColors } from "@/components/ui/ThemePicker";

const DEFAULT_BRAND_COLOR = "#FF5E00"; // CR orange
const DEFAULT_BRAND_COLOR_END = "#FFB300"; // CR bright

function hexToHue(hex: string): number {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 25;
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  if (max !== min) {
    const d = max - min;
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return Math.round(h * 360);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 255, g: 107, b: 0 };
}

// Adjust color lightness - positive amount brightens, negative darkens
function adjustColorLightness(hex: string, amount: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;

  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);

  // Convert to HSL
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  // Adjust lightness
  l = Math.max(0, Math.min(1, l + amount));

  // Convert back to RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const rOut = Math.round(hue2rgb(p, q, h + 1/3) * 255);
  const gOut = Math.round(hue2rgb(p, q, h) * 255);
  const bOut = Math.round(hue2rgb(p, q, h - 1/3) * 255);

  return `#${rOut.toString(16).padStart(2, '0')}${gOut.toString(16).padStart(2, '0')}${bOut.toString(16).padStart(2, '0')}`;
}

interface ProjectCardProps {
  project: Project;
  index?: number;
  onClick: (rect?: DOMRect, clickPos?: { x: number; y: number }) => void;
  isUnlocked?: boolean;
  hideIndex?: boolean;
  noAnimation?: boolean;
  noMagnetic?: boolean; // Disable internal magnetic effect (for when wrapped externally)
  forceHovered?: boolean; // Force hover state from parent (for unit hover behavior)
  overrideBrandColor?: string; // Force a specific brand color instead of using project's
  overrideBrandColorEnd?: string; // Force a specific bright color instead of using project's
  onThemeChange?: (theme: ThemeColors | null) => void; // Callback for mobile touch theme changes
  isLightMode?: boolean;
  compact?: boolean; // Remove internal padding for nav cards
  arrowLeft?: boolean; // Show left arrow instead of right (for "previous" nav)
  trackingSource?: "homepage" | "case_study_nav"; // Where the click originated
}

export function ProjectCard({
  project,
  index = 0,
  onClick,
  isUnlocked = true,
  hideIndex = false,
  noAnimation = false,
  noMagnetic = false,
  forceHovered,
  overrideBrandColor,
  overrideBrandColorEnd,
  onThemeChange,
  isLightMode = false,
  compact = false,
  arrowLeft = false,
  trackingSource = "homepage",
}: ProjectCardProps) {
  const { mode: motionMode } = useMotionMode();
  const [isHoveredInternal, setIsHoveredInternal] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  // If forceHovered is explicitly set (true or false), use it exclusively; otherwise use internal state
  const isExternallyControlled = forceHovered !== undefined;
  const isHovered = isExternallyControlled ? forceHovered : isHoveredInternal;
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const cardRef = useRef<HTMLButtonElement>(null);
  const clientMousePos = useRef({ x: -1000, y: -1000 }); // Track absolute mouse position
  const isLocked = project.isProtected && !isUnlocked;

  // Transition for hover effects - instant when motion is off
  const hoverTransition = motionMode === "off" ? "none" : "opacity var(--duration-normal) var(--ease-out)";
  const hoverTransitionSlow = motionMode === "off" ? "none" : "opacity var(--duration-slow) var(--ease-out)";

  const baseBrandColor = overrideBrandColor || project.brandColor || DEFAULT_BRAND_COLOR;
  const baseBrandColorEnd = overrideBrandColorEnd || project.brandColorEnd || DEFAULT_BRAND_COLOR_END;

  // Adjust colors based on theme: slightly darker for light mode, brighter for dark mode
  const colorAdjustment = isLightMode ? -0.05 : 0.1;
  const brandColor = useMemo(() => adjustColorLightness(baseBrandColor, colorAdjustment), [baseBrandColor, colorAdjustment]);
  const brandColorEnd = useMemo(() => adjustColorLightness(baseBrandColorEnd, colorAdjustment), [baseBrandColorEnd, colorAdjustment]);

  const brandRgb = useMemo(() => hexToRgb(brandColor), [brandColor]);
  const brandHue = useMemo(() => hexToHue(baseBrandColor), [baseBrandColor]);

  // Create theme colors object for this project
  const projectTheme = useMemo((): ThemeColors => ({
    base: brandColor,
    bright: brandColorEnd,
    hue: brandHue,
  }), [brandColor, brandColorEnd, brandHue]);

  // Recalculate mouse position and hover state (called on scroll)
  const updateFromClientPosition = useCallback(() => {
    if (!cardRef.current || clientMousePos.current.x < 0) return;

    const rect = cardRef.current.getBoundingClientRect();
    const { x, y } = clientMousePos.current;

    // Check if mouse is over the card
    const isOver = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

    if (isOver) {
      setMousePos({
        x: (x - rect.left) / rect.width,
        y: (y - rect.top) / rect.height,
      });
      setIsHoveredInternal(true);
      onThemeChange?.(projectTheme);
    } else {
      setIsHoveredInternal(false);
      // Don't call onThemeChange(null) on scroll - let page.tsx handle section theme
    }
  }, [onThemeChange, projectTheme]);

  // Listen for scroll to update hover state and gradient position
  useEffect(() => {
    const handleScroll = () => {
      updateFromClientPosition();
    };

    // Track global mouse position
    const handleGlobalMouseMove = (e: MouseEvent) => {
      clientMousePos.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleGlobalMouseMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [updateFromClientPosition]);

  // Notify parent of theme when forceHovered changes (mobile scroll)
  useEffect(() => {
    if (isExternallyControlled) {
      if (forceHovered) {
        onThemeChange?.(projectTheme);
      }
      // Don't send null on unhover - let next card's theme take over
    }
  }, [forceHovered, isExternallyControlled, onThemeChange, projectTheme]);

  const handleClick = () => {
    // Track case study open with source
    track("case_study_open", { project: project.id, source: trackingSource });
    // Pass card rect and click position for spatial animation
    const rect = cardRef.current?.getBoundingClientRect();
    onClick(rect, { x: clientMousePos.current.x, y: clientMousePos.current.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    clientMousePos.current = { x: e.clientX, y: e.clientY };
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setMousePos({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    }
  };

  const handleTouchStart = () => {
    setIsHoveredInternal(true);
    // Trigger theme change on touch
    onThemeChange?.(projectTheme);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (cardRef.current && e.touches[0]) {
      const rect = cardRef.current.getBoundingClientRect();
      setMousePos({
        x: (e.touches[0].clientX - rect.left) / rect.width,
        y: (e.touches[0].clientY - rect.top) / rect.height,
      });
      setIsHoveredInternal(true);
      // Keep theme active while touching
      onThemeChange?.(projectTheme);
    }
  };

  const handleTouchEnd = () => {
    setIsHoveredInternal(false);
    // Reset theme on touch end
    onThemeChange?.(null);
  };

  // Skip internal hover handlers when externally controlled
  const handleMouseEnter = forceHovered ? undefined : () => {
    setIsHoveredInternal(true);
    onThemeChange?.(projectTheme);
  };
  const handleMouseLeave = forceHovered ? undefined : () => {
    setIsHoveredInternal(false);
    onThemeChange?.(null);
  };

  const cardButton = (
        <button
          ref={cardRef}
          type="button"
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={forceHovered ? undefined : handleTouchStart}
          onTouchMove={forceHovered ? undefined : handleTouchMove}
          onTouchEnd={forceHovered ? undefined : handleTouchEnd}
          onTouchCancel={forceHovered ? undefined : handleTouchEnd}
          onFocus={forceHovered ? undefined : () => setIsHoveredInternal(true)}
          onBlur={forceHovered ? undefined : () => setIsHoveredInternal(false)}
          aria-label={`View ${project.title} case study${isLocked ? " (password protected)" : ""}`}
          data-brand-hue={brandHue}
          data-theme-base={brandColor}
          data-theme-bright={brandColorEnd}
          className="relative w-full h-full overflow-hidden rounded-2xl cursor-pointer text-left glass-card focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] focus-visible:ring-[var(--theme-base)]"
          style={{
            borderColor: isHovered ? brandColor : undefined,
            transition: motionMode === "off" ? "none" : undefined,
          }}
        >
          {/* Glow effect on hover */}
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              boxShadow: `0 0 30px rgba(${brandRgb.r},${brandRgb.g},${brandRgb.b},0.15), 0 0 60px rgba(${brandRgb.r},${brandRgb.g},${brandRgb.b},0.08)`,
              opacity: isHovered ? 1 : 0,
              transition: hoverTransition,
            }}
          />
          {/* Mouse-following gradient - disabled when motion off */}
          {motionMode === "on" && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(600px circle at ${mousePos.x * 100}% ${mousePos.y * 100}%, rgba(${brandRgb.r},${brandRgb.g},${brandRgb.b},0.15) 0%, transparent 100%)`,
                opacity: isHovered ? 1 : 0,
                transition: hoverTransition,
              }}
            />
          )}
          {/* Index number */}
          {!hideIndex && (
            <div className="absolute top-4 left-6 text-[7rem] font-bold leading-none pointer-events-none">
              {/* Base layer */}
              <span
                style={{
                  opacity: isHovered ? 0 : 0.08,
                  color: "var(--foreground)",
                  transition: hoverTransitionSlow,
                }}
              >
                0{index + 1}
              </span>
              {/* Gradient overlay - uses project brand colors */}
              <span
                className="absolute inset-0 animate-gradient-x"
                style={{
                  opacity: isHovered ? 0.25 : 0,
                  backgroundImage: `linear-gradient(90deg, ${brandColor}, ${brandColorEnd}, ${brandColor})`,
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  transition: hoverTransitionSlow,
                }}
              >
                0{index + 1}
              </span>
            </div>
          )}

          {/* Content */}
          <div className={`relative ${compact ? 'p-4' : 'p-[clamp(24px,4vw,48px)]'}`}>
            <div className={`flex flex-col ${compact ? 'gap-2' : 'gap-4'}`}>
              <div className="flex-1">
                <h3 className="relative text-[clamp(1.5rem,3.5vw,2rem)] font-bold mb-2 font-headline text-balance flex items-center gap-2">
                  <span className="relative inline-block">
                    <span className="invisible">{project.title}</span>
                    {/* Base */}
                    <span
                      className="absolute inset-0"
                      style={{
                        opacity: isHovered ? 0 : 1,
                        color: "var(--foreground)",
                        transition: hoverTransition,
                      }}
                    >
                      {project.title}
                    </span>
                    {/* Gradient overlay - uses project brand colors */}
                    <span
                      className="absolute inset-0 animate-gradient-x"
                      style={{
                        opacity: isHovered ? 1 : 0,
                        backgroundImage: `linear-gradient(90deg, ${brandColor}, ${brandColorEnd}, ${brandColor})`,
                        backgroundSize: "200% 100%",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                        transition: hoverTransition,
                      }}
                    >
                      {project.title}
                    </span>
                  </span>
                </h3>
                {project.subtitle && (
                <p className="text-[rgba(var(--foreground-rgb),0.6)] text-[length:var(--text-lg)] mb-4">
                  {project.subtitle.split(new RegExp(`(${project.company}(?:'s)?)`)).map((part, i) =>
                    part.match(new RegExp(`^${project.company}(?:'s)?$`)) ? (
                      <span key={i} className="relative inline-block">
                        <span className="invisible">{part}</span>
                        <span
                          className="absolute inset-0"
                          style={{
                            opacity: isHovered ? 0 : 1,
                            transition: hoverTransition,
                          }}
                        >
                          {part}
                        </span>
                        <span
                          className="absolute inset-0 animate-gradient-x"
                          style={{
                            opacity: isHovered ? 1 : 0,
                            backgroundImage: `linear-gradient(90deg, ${brandColor}, ${brandColorEnd}, ${brandColor})`,
                            backgroundSize: "200% 100%",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            color: "transparent",
                            transition: hoverTransition,
                          }}
                        >
                          {part}
                        </span>
                      </span>
                    ) : part
                  )}
                </p>
                )}

                {/* Metrics/Tags + Action button row */}
                <div className="flex items-end gap-3">
                  {/* Metrics or Tags as pills */}
                  {project.metrics && project.metrics.length > 0 ? (
                  <div className="flex flex-wrap gap-2 flex-1">
                    {project.metrics.map((metric, i) => {
                      // Always shimmer if metric.shimmer is true, otherwise shimmer on hover
                      const shouldShimmer = metric.shimmer || isHovered;
                      return (
                      <div
                        key={i}
                        className="px-3 py-1.5 rounded-full bg-[var(--glass-bg-subtle)] backdrop-blur-[var(--glass-blur-subtle)] border border-[var(--glass-border-subtle)] flex items-center gap-1"
                      >
                        <span className="relative font-medium text-[0.9rem] font-headline tabular-nums">
                          {/* Base */}
                          <span
                            style={{
                              opacity: shouldShimmer ? 0 : 1,
                              color: "rgba(var(--foreground-rgb),0.7)",
                              transition: hoverTransition,
                            }}
                          >
                            {metric.value}
                          </span>
                          {/* Gradient overlay - uses project brand colors */}
                          <span
                            className="absolute inset-0 animate-gradient-x"
                            style={{
                              opacity: shouldShimmer ? 1 : 0,
                              backgroundImage: `linear-gradient(90deg, ${brandColor}, ${brandColorEnd}, ${brandColor})`,
                              backgroundSize: "200% 100%",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              color: "transparent",
                              transition: hoverTransition,
                            }}
                          >
                            {metric.value}
                          </span>
                        </span>
                        {metric.label && (
                        <span className="text-[rgba(var(--foreground-rgb),0.5)] text-[0.75rem]">
                          {metric.label}
                        </span>
                        )}
                      </div>
                    );})}
                  </div>
                  ) : project.tags && project.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2 flex-1">
                    {project.tags.map((tag, i) => (
                      <div
                        key={i}
                        className="px-3 py-1.5 rounded-full bg-[var(--glass-bg-subtle)] backdrop-blur-[var(--glass-blur-subtle)] border border-[var(--glass-border-subtle)]"
                      >
                        <span className="relative text-[0.8rem]">
                          <span
                            style={{
                              opacity: isHovered ? 0 : 1,
                              color: "rgba(var(--foreground-rgb),0.5)",
                              transition: hoverTransition,
                            }}
                          >
                            {tag}
                          </span>
                          <span
                            className="absolute inset-0 animate-gradient-x"
                            style={{
                              opacity: isHovered ? 1 : 0,
                              backgroundImage: `linear-gradient(90deg, ${brandColor}, ${brandColorEnd}, ${brandColor})`,
                              backgroundSize: "200% 100%",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              color: "transparent",
                              transition: hoverTransition,
                            }}
                          >
                            {tag}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                  ) : <div className="flex-1" />}

                  {/* Action button - lock for protected, icon orb, or arrow */}
                  <div
                    className={`glass ${project.ctaLabel ? "px-4 h-[var(--btn-size-icon)]" : "w-[var(--btn-size-icon)] h-[var(--btn-size-icon)]"} rounded-full flex items-center justify-center gap-2 flex-shrink-0 overflow-hidden`}
                    onMouseEnter={() => setIsButtonHovered(true)}
                    onMouseLeave={() => setIsButtonHovered(false)}
                    style={{
                      opacity: 1,
                      backgroundColor: isHovered ? brandColor : undefined,
                      boxShadow: isHovered ? `0 0 20px rgba(${brandRgb.r},${brandRgb.g},${brandRgb.b},0.5), 0 0 40px rgba(${brandRgb.r},${brandRgb.g},${brandRgb.b},0.3)` : undefined,
                      transform: isButtonHovered ? "scale(1.2)" : "scale(1)",
                      transition: motionMode === "off" ? "none" : "all var(--duration-normal) var(--ease-out)",
                      color: isHovered ? (isLightMode ? "#fff" : "#1a1a1a") : "rgba(var(--foreground-rgb),0.7)",
                      isolation: "isolate",
                      backdropFilter: "blur(24px)",
                      WebkitBackdropFilter: "blur(24px)",
                    }}
                  >
                    {project.ctaIcon && !isHovered ? (
                      /* Default state: show icon orb */
                      <Image
                        src={project.ctaIcon}
                        width={56}
                        height={56}
                        alt=""
                        loading="lazy"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (isLocked || project.showLockIcon) ? (
                      /* Hover state for locked/coming-soon projects: show lock */
                      <LockIcon size={16} />
                    ) : (
                      /* Hover state for normal projects: show arrow */
                      <>
                        {project.ctaLabel && (
                          <span className="text-sm font-medium whitespace-nowrap">{project.ctaLabel}</span>
                        )}
                        {arrowLeft ? (
                          <BackIcon size={16} />
                        ) : (
                          <ArrowRightIcon size={16} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </button>
  );

  const card = noMagnetic ? cardButton : (
    <Magnetic strength={0.06} tiltStrength={2} className="h-full">
      {cardButton}
    </Magnetic>
  );

  if (noAnimation) {
    return card;
  }

  return <Section delay={index * 100} threshold="top 85%" className="h-full">{card}</Section>;
}
